
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const Licenses = () => {
  const { t } = useTranslation();

  const licenses = [
    {
      title: 'Government of India Authorization',
      number: 'B-1701/KER/PER/1000+/5/9853/2021',
      authority: 'Approved by Government of India, Ministry of External Affairs',
      description: 'Official authorization for overseas recruitment and placement services',
      icon: 'ri-government-line'
    },
    {
      title: 'Kerala State License',
      number: 'MHA-2024-001',
      authority: 'Government of Kerala',
      description: 'State-level authorization for recruitment agency operations',
      icon: 'ri-building-line'
    },
    {
      title: 'GCC Operations License',
      number: 'MHA-2024-002',
      authority: 'GCC Regulatory Authority',
      description: 'Authorization for recruitment operations across GCC countries',
      icon: 'ri-global-line'
    },
    {
      title: 'International Placement Certification',
      number: 'MHA-2024-003',
      authority: 'International Labor Organization',
      description: 'Certification for international job placement services',
      icon: 'ri-award-line'
    }
  ];

  const certifications = [
    'ISO 9001:2015 Quality Management System',
    'Member of Association of Recruitment Consultancies',
    'Certified by Ministry of External Affairs',
    'Registered with Protector of Emigrants',
    'Approved by Qatar Ministry of Labor',
    'Licensed by Kuwait Ministry of Social Affairs'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('licensesTitle')}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t('licensesSubtitle')}
          </p>
        </div>
      </section>

      {/* Primary License */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-blue-600 text-3xl w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Official Government Authorization</h2>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <p className="text-lg font-semibold text-gray-900 mb-2">
                Licence No.: B-1701/KER/PER/1000+/5/9853/2021
              </p>
              <p className="text-gray-700">
                Approved by Government of India, Ministry of External Affairs
              </p>
            </div>
            <p className="text-gray-600 leading-relaxed">
              This license authorizes Bicha Travels to conduct overseas recruitment and placement services, 
              ensuring full compliance with Indian government regulations and international labor standards.
            </p>
          </div>
        </div>
      </section>

      {/* All Licenses */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Licenses & Authorizations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We maintain all necessary licenses and certifications to operate legally and ethically
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {licenses.map((license, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${license.icon} text-blue-600 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {license.title}
                    </h3>
                    <p className="text-blue-600 font-medium mb-2">
                      {license.number}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      {license.authority}
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {license.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Additional Certifications
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence is reflected in our comprehensive certifications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center hover:shadow-md transition-shadow duration-200">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-gray-900 font-medium">
                  {cert}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Statement */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Our Commitment to Compliance
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed">
            Bicha Travels operates in full compliance with all applicable laws and regulations 
            governing overseas recruitment. We are committed to ethical practices, transparency, 
            and the protection of workers' rights throughout the recruitment process.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Licenses;